import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styles from "./BrandAnalysis.module.scss";
import { Card, Empty, Input, Select, Skeleton, Table, Typography } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  BrandAnalysisLanding,
  BrandAnalysisLandingItem,
  BrandAnalysisPotentialBrand,
  getAllBranchOffices,
  getAllChannels,
  getBrandAnalysisLanding,
} from "../../../api/Services";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../../helpers/FilterSearchHelper";
import {
  isVariationAvailable,
  variationNotAvailableLabel,
} from "../../../helpers/VariationHelper";

const { Title, Text } = Typography;

const fmt = (v: number) =>
  `$${(v ?? 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => (v ?? 0).toLocaleString("es-AR");

const Var: React.FC<{ value?: number | null; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (!isVariationAvailable(value)) return <span>{variationNotAvailableLabel()}</span>;
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}
    >
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      {" "}{pos ? "+" : ""}{value.toFixed(1)}{suffix}
    </span>
  );
};

const emptyLanding: BrandAnalysisLanding = {
  brands: [],
  dependency: { top1: null, top3: null, top5: null },
  potentialBrands: [],
};

const LandingTableSkeleton: React.FC = () => (
  <div className={styles.tableSkeleton}>
    {Array.from({ length: 6 }).map((_, i) => (
      <Skeleton
        key={i}
        active
        title={false}
        paragraph={{ rows: 1, width: ["100%"] }}
      />
    ))}
  </div>
);

const BrandAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const [period, setPeriod] = useState(30);
  const [channelId, setChannelId] = useState<number | undefined>();
  const [branchOfficeId, setBranchOfficeId] = useState<number | undefined>();
  const [sortField, setSortField] = useState("grossSales");
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend">("descend");
  const [data, setData] = useState<BrandAnalysisLanding>(emptyLanding);
  const [loading, setLoading] = useState(true);
  const [channelOptions, setChannelOptions] = useState<{ value: number; label: string }[]>([]);
  const [storeOptions, setStoreOptions] = useState<{ value: number; label: string }[]>([]);
  const [filtersReady, setFiltersReady] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setFiltersLoading(true);
    (async () => {
      const stores = await getAllBranchOffices();
      if (cancelled) return;
      setStoreOptions(
        (stores || [])
          .map((s: any) => ({ value: s.id, label: s.name }))
          .filter((s: any) => s.value)
      );
      const channels = await getAllChannels();
      if (cancelled) return;
      setChannelOptions(
        (channels || [])
          .map((c: any) => ({
            value: c.id,
            label: c?.type?.descriptionEs || c?.type?.type || c?.name || `${t("BrandAnalysis-Channel")} ${c.id}`,
          }))
          .filter((c: any) => c.value)
      );
      setFiltersReady(true);
      setFiltersLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!filtersReady) return;
    const requestId = beginRequest();
    setLoading(true);
    (async () => {
      const result = await getBrandAnalysisLanding({
        search: debouncedSearch || undefined,
        period,
        channelId,
        branchOfficeId,
        sortBy: sortField,
        sortOrder: sortOrder === "ascend" ? "asc" : "desc",
      });
      if (isStaleRequest(requestId)) return;
      setData(result);
      setLoading(false);
    })();
  }, [
    filtersReady,
    debouncedSearch,
    period,
    channelId,
    branchOfficeId,
    sortField,
    sortOrder,
    beginRequest,
    isStaleRequest,
  ]);

  const rows = useMemo(() => data.brands || [], [data.brands]);
  const hasData = rows.length > 0;

  const columns = [
    {
      title: t("BrandAnalysis-Brand"),
      key: "name",
      render: (_: any, r: BrandAnalysisLandingItem) => (
        <div className={styles.brandCell}>
          <div className={styles.brandText}>
            <Text strong className={styles.brandName}>
              {r.name}
            </Text>
            {(r.risk || r.potential) && (
              <div className={styles.brandTags}>
                {r.potential && (
                  <span className={`${styles.miniTag} ${styles.tagPotential}`}>
                    {t("BrandAnalysis-Potential")}
                  </span>
                )}
                {r.risk && (
                  <span className={`${styles.miniTag} ${styles.tagRisk}`}>
                    {t("BrandAnalysis-AtRisk")}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t("BrandAnalysis-GrossSales"),
      key: "grossSales",
      sorter: true,
      sortOrder: sortField === "grossSales" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: BrandAnalysisLandingItem) => (
        <div className={styles.cellStack}>
          <Text strong>{fmt(r.grossSales)}</Text>
          <Var value={r.grossSalesVar} />
        </div>
      ),
    },
    {
      title: t("BrandAnalysis-Units"),
      key: "units",
      sorter: true,
      sortOrder: sortField === "units" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: BrandAnalysisLandingItem) => (
        <div className={styles.cellStack}>
          <Text strong>{fmtN(r.units)}</Text>
          <Var value={r.unitsVar} />
        </div>
      ),
    },
    {
      title: t("BrandAnalysis-Margin"),
      key: "marginPct",
      sorter: true,
      sortOrder: sortField === "marginPct" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: BrandAnalysisLandingItem) => (
        <div className={styles.cellStack}>
          <Text strong>{(r.marginPct ?? 0).toFixed(1)}%</Text>
          <Var value={r.marginVar} suffix=" pp" />
        </div>
      ),
    },
    {
      title: t("BrandAnalysis-ActiveProducts"),
      key: "activeProducts",
      sorter: true,
      sortOrder: sortField === "activeProducts" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: BrandAnalysisLandingItem) => (
        <div className={styles.cellStack}>
          <Text strong>{fmtN(r.activeProducts)}</Text>
          <Var value={r.activeProductsVar} />
        </div>
      ),
    },
    {
      title: t("BrandAnalysis-SalesPercent"),
      key: "pctGlobalSales",
      sorter: true,
      sortOrder: sortField === "pctGlobalSales" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: BrandAnalysisLandingItem) => (
        <Text strong className={styles.shareValue}>
          {(r.pctGlobalSales ?? 0).toFixed(1)}%
        </Text>
      ),
    },
    {
      title: t("BrandAnalysis-Risk"),
      key: "risk",
      width: 170,
      render: (_: any, r: BrandAnalysisLandingItem) => (
        <span
          className={`${styles.riskPill} ${
            r.risk ? styles.riskNegative : styles.riskNeutral
          }`}
        >
          {r.riskLabel}
        </span>
      ),
    },
  ];

  const handleTableChange = (_p: any, _f: any, sorter: any) => {
    if (sorter.columnKey) {
      setSortField(sorter.columnKey);
      setSortOrder(sorter.order || "descend");
    }
  };

  const dependencyRows = [
    data.dependency?.top1,
    data.dependency?.top3,
    data.dependency?.top5,
  ].filter(Boolean) as { label: string; pctGlobalSales: number }[];

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>
        {t("BrandAnalysis-Brands")}
      </Title>

      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <Input
            placeholder={t("BrandAnalysis-SearchBrand")}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
            allowClear
          />
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("BrandAnalysis-Period")}</Text>
            <Select
              value={period}
              onChange={setPeriod}
              className={styles.filterSelect}
              options={[
                { value: 30, label: t("BrandAnalysis-Last30Days") },
                { value: 60, label: t("BrandAnalysis-Last60Days") },
                { value: 90, label: t("BrandAnalysis-Last90Days") },
                { value: 180, label: t("BrandAnalysis-Last180Days") },
              ]}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("BrandAnalysis-Channel")}</Text>
            <Select
              placeholder={t("BrandAnalysis-AllChannels")}
              allowClear
              value={channelId}
              onChange={setChannelId}
              className={styles.filterSelect}
              options={channelOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("BrandAnalysis-Store")}</Text>
            <Select
              placeholder={t("BrandAnalysis-AllStores")}
              allowClear
              loading={filtersLoading}
              value={branchOfficeId}
              onChange={setBranchOfficeId}
              className={styles.filterSelect}
              options={storeOptions}
            />
          </div>
        </div>
      </Card>

      <Card className={styles.tableCard}>
        {loading && !hasData ? (
          <LandingTableSkeleton />
        ) : (
          <Table
            columns={columns}
            dataSource={rows.map((b) => ({ ...b, key: b.id }))}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            size="small"
            className={styles.dataTable}
            loading={loading && hasData}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => navigate(`/commercial-analysis/brands/${record.id}`),
              style: { cursor: "pointer" },
            })}
            scroll={{ x: 1100 }}
            locale={{ emptyText: <Empty description={t("BrandAnalysis-NoBrands")} /> }}
          />
        )}
      </Card>

      <div className={styles.insightsRow}>
        <Card className={styles.insightCard}>
          <Title level={5} className={styles.insightTitle}>
            {t("BrandAnalysis-BusinessDependency")}
          </Title>
          {loading ? (
            <div className={styles.dependencyList}>
              {[100, 70, 40].map((w, i) => (
                <div className={styles.dependencyRow} key={i}>
                  <div className={styles.dependencyHeader}>
                    <Skeleton.Input active size="small" style={{ width: 90, height: 14 }} />
                    <Skeleton.Input active size="small" style={{ width: 36, height: 14 }} />
                  </div>
                  <Skeleton.Input active style={{ height: 8, borderRadius: 999, width: `${w}%` }} />
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.dependencyList}>
              {dependencyRows.map((row) => (
                <div className={styles.dependencyRow} key={row.label}>
                  <div className={styles.dependencyHeader}>
                    <Text className={styles.dependencyLabel}>{row.label}</Text>
                    <Text strong className={styles.dependencyValue}>
                      {row.pctGlobalSales.toFixed(1)}%
                    </Text>
                  </div>
                  <div className={styles.barTrack}>
                    <div
                      className={styles.barFill}
                      style={{ width: `${Math.min(row.pctGlobalSales, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className={styles.insightCard}>
          <Title level={5} className={styles.insightTitle}>
            {t("BrandAnalysis-UnexploitedPotentialBrands")}
          </Title>
          {loading ? (
            <div className={styles.potentialList}>
              {[0, 1].map((i) => (
                <div className={styles.potentialItem} key={i}>
                  <Skeleton.Input active size="small" style={{ width: "50%", height: 16, marginBottom: 6 }} />
                  <Skeleton.Input active size="small" style={{ width: "90%", height: 13 }} />
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.potentialList}>
              {(data.potentialBrands || []).length === 0 ? (
                <Text type="secondary">{t("BrandAnalysis-NoPotentialBrands")}</Text>
              ) : (
                data.potentialBrands.map((b: BrandAnalysisPotentialBrand) => (
                  <div
                    key={b.id}
                    className={styles.potentialItem}
                    onClick={() => navigate(`/commercial-analysis/brands/${b.id}`)}
                  >
                    <Text strong className={styles.potentialName}>
                      {b.name}
                    </Text>
                    <Text className={styles.potentialText}>{b.explanation}</Text>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default BrandAnalysis;
